import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    Box, Paper, Typography, CircularProgress, 
    Button, Grid, Divider, IconButton, Card, CardContent, Chip
} from '@mui/material';
import { 
    Edit, ArrowBack, Phone, Email, LocationOn, 
    WorkOutline, Note, Delete 
} from '@mui/icons-material';
import api from '../../api/axios';
import { format } from 'date-fns';

const ClientDetailView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [jobs, setJobs] = useState([]);

    useEffect(() => {
        loadClient();
    }, [id]);

    const loadClient = async () => {
        setLoading(true);
        try {
            const [clientRes, jobsRes] = await Promise.all([
                api.get(`/admin/clients/${id}`),
                api.get(`/admin/jobs`) // Ideally filter by client_id in backend, but for now client-side or check if backend supports it
            ]);
            
            setClient(clientRes.data);
            
            // Filter jobs for this client (if backend doesn't support filtering by client yet)
            // Note: In a real app with many jobs, we should add ?client_id=X to the jobs endpoint
            const clientJobs = jobsRes.data.filter(j => j.client_id === parseInt(id));
            setJobs(clientJobs);
            
        } catch (err) {
            console.error('Failed to load client details:', err);
        }
        setLoading(false);
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this client? This cannot be undone.')) {
            try {
                await api.delete(`/admin/clients/${id}`);
                navigate('/clients');
            } catch (err) {
                console.error('Failed to delete client:', err);
                alert('Failed to delete client. They may have active jobs.');
            }
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    if (!client) return <Box sx={{ p: 4 }}><Typography>Client not found</Typography></Box>;

    return (
        <Box sx={{ pb: 8 }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={() => navigate('/clients')} sx={{ mr: 2 }}>
                        <ArrowBack />
                    </IconButton>
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            {client.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Client ID: #{client.id}
                        </Typography>
                    </Box>
                </Box>
                <Box>
                    <Button 
                        variant="outlined" 
                        color="error"
                        startIcon={<Delete />}
                        onClick={handleDelete}
                        sx={{ mr: 1 }}
                    >
                        Delete
                    </Button>
                    <Button 
                        variant="outlined" 
                        startIcon={<Edit />}
                        onClick={() => navigate(`/clients/${id}/edit`)}
                    >
                        Edit
                    </Button>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* Contact Info Card */}
                <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 3, border: '1px solid #E2E8F0', borderRadius: 3 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                            Contact Details
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, mt: 2 }}>
                            <Email color="action" sx={{ mr: 2, mt: 0.5 }} />
                            <Box>
                                <Typography variant="caption" color="text.secondary">Email</Typography>
                                <Typography variant="body1">
                                    {client.email ? (
                                        <a href={`mailto:${client.email}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                            {client.email}
                                        </a>
                                    ) : 'No email provided'}
                                </Typography>
                            </Box>
                        </Box>
                        
                        <Divider sx={{ my: 1 }} />
                        
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, mt: 2 }}>
                            <Phone color="action" sx={{ mr: 2, mt: 0.5 }} />
                            <Box>
                                <Typography variant="caption" color="text.secondary">Phone</Typography>
                                <Typography variant="body1">
                                    {client.phone_number ? (
                                        <a href={`tel:${client.phone_number}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                            {client.phone_number}
                                        </a>
                                    ) : 'No phone provided'}
                                </Typography>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 1 }} />
                        
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 2 }}>
                            <LocationOn color="action" sx={{ mr: 2, mt: 0.5 }} />
                            <Box>
                                <Typography variant="caption" color="text.secondary">Address</Typography>
                                <Typography variant="body1">
                                    {client.address || 'No address provided'}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>

                    {/* Notes Card */}
                    <Paper elevation={0} sx={{ p: 3, mt: 3, border: '1px solid #E2E8F0', borderRadius: 3 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                            <Note sx={{ mr: 1, fontSize: 20 }} /> Notes
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                            {client.notes || 'No notes for this client.'}
                        </Typography>
                    </Paper>
                </Grid>

                {/* Job History */}
                <Grid item xs={12} md={8}>
                    <Paper elevation={0} sx={{ p: 3, border: '1px solid #E2E8F0', borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Typography variant="h6" fontWeight={600}>
                                Job History ({jobs.length})
                            </Typography>
                            <Button 
                                variant="contained" 
                                size="small" 
                                onClick={() => navigate(`/jobs/new?client_id=${client.id}`)}
                            >
                                New Job
                            </Button>
                        </Box>

                        {jobs.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                                <WorkOutline sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                                <Typography>No jobs found for this client</Typography>
                            </Box>
                        ) : (
                            <Grid container spacing={2}>
                                {jobs.map(job => (
                                    <Grid item xs={12} key={job.id}>
                                        <Card 
                                            variant="outlined" 
                                            sx={{ 
                                                '&:hover': { bgcolor: '#F8FAFC', cursor: 'pointer' },
                                                borderRadius: 2
                                            }}
                                            onClick={() => navigate(`/jobs/${job.id}`)}
                                        >
                                            <CardContent sx={{ pb: '16px !important' }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <Box>
                                                        <Typography variant="subtitle1" fontWeight={600}>
                                                            {job.description}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                                            <LocationOn sx={{ fontSize: 14, mr: 0.5 }} />
                                                            {job.location}
                                                        </Typography>
                                                    </Box>
                                                    <Chip 
                                                        label={job.status} 
                                                        size="small" 
                                                        color={job.status === 'Completed' ? 'success' : 'default'}
                                                        variant={job.status === 'Completed' ? 'filled' : 'outlined'}
                                                    />
                                                </Box>
                                                <Box sx={{ mt: 2, display: 'flex', gap: 2, fontSize: '0.8rem', color: 'text.secondary' }}>
                                                    <Typography variant="caption">
                                                        Created: {format(new Date(job.created_at || new Date()), 'MMM d, yyyy')}
                                                    </Typography>
                                                    {job.scheduled_date && (
                                                        <Typography variant="caption">
                                                            Scheduled: {format(new Date(job.scheduled_date), 'MMM d, yyyy h:mm a')}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ClientDetailView;
